import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Save, TrendingUp, RefreshCw, Calculator, Globe, 
  ExternalLink, DollarSign, AlertCircle, CheckCircle, Clock,
  Edit3, X, Plus, Trash2
} from 'lucide-react';
import { GoldRate } from '../types';

interface AdminPricingProps {
  rates?: GoldRate[];
  onUpdateRates?: (newRates: GoldRate[]) => void;
  onNavigate: (page: string) => void;
}

const AdminPricing: React.FC<AdminPricingProps> = ({ 
  rates = [], 
  onUpdateRates, 
  onNavigate 
}) => {
  const [localRates, setLocalRates] = useState<GoldRate[]>([]);
  const [originalRates, setOriginalRates] = useState<GoldRate[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  // API base URL
  const API_BASE_URL = process.env.NODE_ENV === 'production' 
    ? 'https://sparklinggems.shop/api'
    : 'http://localhost/sparkling-gems-backend-php/api';

  // Fetch rates from database on component mount
  useEffect(() => {
    fetchRatesFromDatabase();
  }, []);

  // Fetch rates from database
  const fetchRatesFromDatabase = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/gold-rates/index.php`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      console.log('📊 Gold rates API response:', data);
      
      // Handle different response structures
      const ratesArray = data.rates || data.data || data;
      
      if (Array.isArray(ratesArray) && ratesArray.length > 0) {
        // Handle both camelCase and snake_case responses
        const formattedRates = ratesArray.map((rate: any) => ({
          id: rate.id,
          goldType: rate.goldType || rate.gold_type || '',
          karat: rate.karat,
          pricePerGram: parseFloat(rate.pricePerGram || rate.price_per_gram || 0),
          lastUpdated: rate.lastUpdated || rate.last_updated || new Date().toISOString()
        }));
        
        console.log('✅ Formatted gold rates:', formattedRates);
        setLocalRates(formattedRates);
        setOriginalRates(JSON.parse(JSON.stringify(formattedRates))); // Deep copy
      } else if (rates.length > 0) {
        // Fallback to props
        console.log('⚠️ Using fallback rates from props');
        setLocalRates(rates);
        setOriginalRates(JSON.parse(JSON.stringify(rates)));
      } else {
        console.log('❌ No rates found');
        setLocalRates([]);
        setOriginalRates([]);
      }
    } catch (error) {
      console.error('❌ Error fetching gold rates:', error);
      setError('Failed to load gold rates. Please try again.');
      
      // Fallback to props if available
      if (rates.length > 0) {
        setLocalRates(rates);
        setOriginalRates(JSON.parse(JSON.stringify(rates)));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Save rates to database
  const saveRatesToDatabase = async (updatedRates: GoldRate[]): Promise<boolean> => {
    try {
      console.log('💾 Saving rates to database:', updatedRates);
      
      // Prepare data for API - convert to snake_case for backend
      const ratesForApi = updatedRates.map(rate => ({
        id: rate.id,
        gold_type: rate.goldType,
        karat: rate.karat,
        price_per_gram: rate.pricePerGram
      }));

      const response = await fetch(`${API_BASE_URL}/gold-rates/update.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rates: ratesForApi })
      });
      
      const data = await response.json();
      console.log('💾 Save response:', data);
      
      return data.success === true;
    } catch (error) {
      console.error('❌ Error saving rates:', error);
      return false;
    }
  };

  // Handle price change
  const handlePriceChange = (id: number, newPrice: string) => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) return;

    setLocalRates(prev => prev.map(rate => 
      rate.id === id ? { ...rate, pricePerGram: price } : rate
    ));
  };

  // Handle edit start
  const handleEditStart = (rate: GoldRate) => {
    setEditingId(rate.id);
    setEditValue(rate.pricePerGram.toString());
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  // Handle edit save
  const handleEditSave = (id: number) => {
    const price = parseFloat(editValue);
    if (!isNaN(price) && price >= 0) {
      handlePriceChange(id, editValue);
    }
    setEditingId(null);
  };

  // Handle key press (Enter to save, Escape to cancel)
  const handleKeyDown = (e: React.KeyboardEvent, id: number) => {
    if (e.key === 'Enter') {
      handleEditSave(id);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  // Check if there are unsaved changes
  const hasChanges = (): boolean => {
    if (localRates.length !== originalRates.length) return true;
    
    return localRates.some((rate, index) => {
      const original = originalRates[index];
      return original && Math.abs(rate.pricePerGram - original.pricePerGram) > 0.01;
    });
  };

  // Get changed rates
  const getChangedRates = (): GoldRate[] => {
    return localRates.filter((rate, index) => {
      const original = originalRates[index];
      return original && Math.abs(rate.pricePerGram - original.pricePerGram) > 0.01;
    });
  };

  // Reset to original values
  const handleReset = () => {
    if (window.confirm('Discard all unsaved changes?')) {
      setLocalRates(JSON.parse(JSON.stringify(originalRates)));
      setEditingId(null);
      setError(null);
      setSuccess(null);
    }
  };

  // Save all changes
  const handleSave = async () => {
    if (!hasChanges()) {
      setSuccess('No changes to save');
      setTimeout(() => setSuccess(null), 3000);
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const changedRates = getChangedRates();
      console.log('💾 Saving changed rates:', changedRates);
      
      // Save to database
      const saved = await saveRatesToDatabase(changedRates);
      
      if (saved) {
        // Update timestamps
        const now = new Date().toISOString();
        const updatedWithTimestamp = localRates.map(rate => ({
          ...rate,
          lastUpdated: now
        }));
        
        setLocalRates(updatedWithTimestamp);
        setOriginalRates(JSON.parse(JSON.stringify(updatedWithTimestamp)));
        setLastSaved(new Date());
        setSuccess(`Successfully updated ${changedRates.length} gold rate(s)`);
        
        // CRITICAL: Notify parent component to update products
        if (onUpdateRates) {
          console.log('🔄 Notifying parent of rate changes...');
          onUpdateRates(updatedWithTimestamp);
        }
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error('Database update failed');
      }
    } catch (error) {
      console.error('❌ Error in save:', error);
      setError('Failed to update gold rates. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Group rates by Gold Type
  const groupedRates = localRates.reduce((acc, rate) => {
    const key = rate.goldType || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(rate);
    return acc;
  }, {} as Record<string, GoldRate[]>);

  // Sort karats in logical order
  const sortKarats = (rates: GoldRate[]): GoldRate[] => {
    const karatOrder: { [key: string]: number } = {
      '24K': 1, '24k': 1,
      '22K': 2, '22k': 2,
      '21K': 3, '21k': 3,
      '20K': 4, '20k': 4,
      '18K': 5, '18k': 5,
      '14K': 6, '14k': 6,
      '12K': 7, '12k': 7,
      '10K': 8, '10k': 8,
    };
    
    return [...rates].sort((a, b) => {
      const orderA = karatOrder[a.karat] || 999;
      const orderB = karatOrder[b.karat] || 999;
      return orderA - orderB;
    });
  };

  // Format gold type for display
  const formatGoldType = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      'Saudi Gold': 'Saudi Gold',
      'Japan Gold': 'Japan Gold',
      'Italian Gold': 'Italian Gold',
      'China Gold': 'China Gold',
      'Local PH Gold': 'Local PH Gold',
      'Ongpin Gold': 'Ongpin Gold'
    };
    return typeMap[type] || type;
  };

  // Get gold type color
  const getGoldTypeColor = (type: string): string => {
    const colorMap: { [key: string]: string } = {
      'Saudi Gold': 'bg-amber-50 text-amber-700 border-amber-200',
      'Japan Gold': 'bg-stone-50 text-stone-700 border-stone-200',
      'Italian Gold': 'bg-rose-50 text-rose-700 border-rose-200',
      'China Gold': 'bg-slate-50 text-slate-700 border-slate-200',
      'Local PH Gold': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Ongpin Gold': 'bg-gold-50 text-gold-700 border-gold-200'
    };
    return colorMap[type] || 'bg-stone-50 text-stone-700 border-stone-200';
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="pt-44 pb-12 min-h-screen bg-stone-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <RefreshCw size={32} className="animate-spin text-gold-600 mx-auto mb-4" />
              <p className="text-stone-500">Loading gold rates...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-44 pb-12 min-h-screen bg-stone-50">
      <div className="max-w-6xl mx-auto px-6">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <button 
              onClick={() => onNavigate('admin-dashboard')} 
              className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors group"
            >
              <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> 
              Back to Dashboard
            </button>
            <h1 className="font-serif text-3xl text-stone-900 flex items-center gap-3">
              Daily Gold Pricing <TrendingUp size={24} className="text-gold-600" />
            </h1>
            <p className="text-stone-500 font-light mt-2 max-w-xl text-sm">
              Adjusting rates here will instantly update the calculation formula for all jewelry items 
              in the inventory based on their weight (grams).
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row items-end md:items-center gap-4">
            {/* Live Market Link */}
            <a 
              href="https://www.kitco.com/charts/gold" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-5 py-3 bg-white border border-gold-200 rounded-sm hover:border-gold-500 hover:shadow-md transition-all duration-300"
            >
              <div className="bg-gold-50 p-2 rounded-full text-gold-600 group-hover:bg-gold-600 group-hover:text-white transition-colors">
                <Globe size={18} />
              </div>
              <div className="text-left">
                <span className="block text-[10px] text-stone-400 uppercase tracking-wider font-bold">Market Watch</span>
                <div className="flex items-center gap-1 text-stone-900 font-serif text-sm">
                  Check Live Spot Price <ExternalLink size={12} className="text-stone-400"/>
                </div>
              </div>
            </a>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {hasChanges() && (
                <button 
                  onClick={handleReset}
                  className="px-4 py-3 bg-white border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors flex items-center gap-2"
                >
                  <X size={14} /> Cancel
                </button>
              )}
              <button 
                onClick={handleSave}
                disabled={isSaving || !hasChanges()}
                className="px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors flex items-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                {isSaving ? 'Saving...' : hasChanges() ? `Save ${getChangedRates().length} Change${getChangedRates().length !== 1 ? 's' : ''}` : 'Saved'}
              </button>
            </div>
          </div>
        </div>
        
        {/* Status Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-sm flex items-center gap-3">
            <AlertCircle size={20} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 p-4 rounded-sm flex items-center gap-3">
            <CheckCircle size={20} />
            <span className="text-sm">{success}</span>
          </div>
        )}
        
        {lastSaved && (
          <div className="mb-6 flex justify-end">
            <span className="text-[10px] text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100 flex items-center gap-2">
              <Clock size={10} />
              Last updated: {lastSaved.toLocaleTimeString()}
            </span>
          </div>
        )}

        {/* Summary Card */}
        <div className="bg-gradient-to-r from-gold-50 to-stone-50 border border-gold-200 p-6 rounded-sm mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Calculator className="text-gold-600" size={24} />
            <h2 className="font-serif text-xl text-stone-900">Price Calculation Formula</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div className="bg-white p-4 rounded-sm border border-stone-200">
              <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">Formula</span>
              <code className="text-stone-900 font-mono">Price = Weight (g) × Gold Rate</code>
            </div>
            <div className="bg-white p-4 rounded-sm border border-stone-200">
              <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">Example</span>
              <code className="text-stone-900 font-mono">5g × ₱4,150 = ₱20,750</code>
            </div>
            <div className="bg-white p-4 rounded-sm border border-stone-200">
              <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">Total Products</span>
              <span className="text-2xl font-serif text-stone-900">{localRates.length} rates</span>
            </div>
          </div>
        </div>

        {/* Rates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(groupedRates).length > 0 ? (
            Object.entries(groupedRates).map(([type, typeRates]) => (
              <div key={type} className="bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden flex flex-col h-full">
                <div className={`px-6 py-4 border-b border-stone-100 flex justify-between items-center ${getGoldTypeColor(type)}`}>
                  <h3 className="font-serif text-lg">{formatGoldType(type)}</h3>
                  <Calculator size={16} className="opacity-50" />
                </div>
                <div className="p-6 space-y-4 flex-1">
                  {sortKarats(typeRates).map((rate) => {
                    const original = originalRates.find(r => r.id === rate.id);
                    const isChanged = original && Math.abs(rate.pricePerGram - original.pricePerGram) > 0.01;
                    const isEditing = editingId === rate.id;
                    
                    return (
                      <div key={rate.id} className="flex items-center gap-4 group">
                        <div className="w-20 flex-shrink-0">
                          <div className={`py-2 px-3 border rounded text-xs font-bold text-center transition-colors ${
                            rate.karat.includes('24') ? 'bg-gold-600 text-white border-gold-600' :
                            rate.karat.includes('22') || rate.karat.includes('21') ? 'bg-gold-50 text-gold-700 border-gold-200' :
                            'bg-stone-50 text-stone-600 border-stone-200'
                          }`}>
                            {rate.karat}
                          </div>
                        </div>
                        
                        <div className="flex-1 relative">
                          <label className="absolute -top-2 left-2 px-1 bg-white text-[9px] uppercase tracking-widest text-stone-400 font-bold">
                            ₱/gram
                          </label>
                          
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, rate.id)}
                                onBlur={() => handleEditSave(rate.id)}
                                step="0.01"
                                min="0"
                                className="w-full border-2 border-gold-500 px-3 py-3 text-sm focus:outline-none rounded-sm"
                                autoFocus
                              />
                            </div>
                          ) : (
                            <div 
                              className={`w-full border px-3 py-3 text-sm flex items-center justify-between cursor-pointer hover:border-gold-500 transition-colors ${
                                isChanged ? 'border-gold-500 bg-gold-50/50' : 'border-stone-200'
                              }`}
                              onClick={() => handleEditStart(rate)}
                            >
                              <span className="font-mono">₱{rate.pricePerGram.toFixed(2)}</span>
                              <Edit3 size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-stone-400" />
                            </div>
                          )}
                        </div>
                        
                        {isChanged && !isEditing && (
                          <div className="w-2 h-2 bg-gold-500 rounded-full animate-pulse" title="Unsaved change" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-12 bg-white border border-stone-200 rounded-sm">
              <AlertCircle size={48} className="mx-auto text-stone-400 mb-4" />
              <p className="text-stone-500">No gold rates found</p>
              <p className="text-xs text-stone-400 mt-2">Please add gold rates in the database</p>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 p-6 rounded-sm">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-bold text-blue-800 uppercase tracking-wider mb-2">
                How Gold Pricing Works
              </h3>
              <ul className="text-sm text-blue-700 space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                  Product prices are automatically calculated based on: <strong>Weight (g) × Gold Rate</strong>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                  Changing gold rates will update all affected products immediately in the shop
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                  Unsaved changes are highlighted with a <span className="w-2 h-2 bg-gold-500 rounded-full inline-block mx-1"></span> gold dot
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                  <strong>Note:</strong> Products with "Manual Price Override" enabled will not be auto-updated
                </li>
              </ul>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminPricing;